import React, { useRef } from 'react';
import { useScrollFade } from '../../hooks/useScrollFade';
import './SubTabs.css';

/**
 * SubTabs — the CRM's one tab component: a segmented pill (spec §3.7). The track is a
 * sunken pill; the active tab is a white knob (not yellow). Used for hub pages (Students,
 * Sales, Inbox), the course workspace and every other in-page tab strip.
 *
 *   <SubTabs tabs={[{ key: 'all', label: 'Hammasi', count: 12 }]} active={tab} onChange={setTab} ariaLabel="…" />
 *
 * tabs: [{ key, label, count?, badge?, disabled? }]
 *
 * Below 1024px the track is full width and the tabs share it equally; when the labels do not
 * fit the strip scrolls with a faded edge and keeps the active tab in view. Arrow keys,
 * Home and End move between tabs (the focused tab becomes the active one), as in the platform.
 */
function SubTabs({ tabs, active, onChange, className = '', ariaLabel }) {
  const { ref, fade } = useScrollFade({ activeKey: active, watch: tabs.length });
  const buttons = useRef({});

  const enabled = tabs.filter((tab) => !tab.disabled);
  // Roving tab stop: the active tab; when none matches (a hub still resolving its route), the first
  // enabled one, so the strip is never skipped by the keyboard.
  const stopKey = enabled.some((tab) => tab.key === active) ? active : enabled[0]?.key;

  const onKeyDown = (e, tab) => {
    const at = enabled.findIndex((x) => x.key === tab.key);
    let next = null;
    if (e.key === 'ArrowRight') next = enabled[(at + 1) % enabled.length];
    else if (e.key === 'ArrowLeft') next = enabled[(at - 1 + enabled.length) % enabled.length];
    else if (e.key === 'Home') next = enabled[0];
    else if (e.key === 'End') next = enabled[enabled.length - 1];
    if (!next) return;
    e.preventDefault();
    onChange(next.key);
    const button = buttons.current[next.key];
    if (button) button.focus();
  };

  return (
    <div className={`subtabs ${className}`.trim()}>
      <div className={`subtabs-row ${fade}`.trim()} role="tablist" aria-label={ariaLabel} ref={ref}>
        {tabs.map((tab) => {
          const isActive = active === tab.key;
          return (
            <button
              key={tab.key}
              ref={(node) => { buttons.current[tab.key] = node; }}
              type="button"
              role="tab"
              aria-selected={isActive}
              tabIndex={tab.key === stopKey ? 0 : -1}
              disabled={tab.disabled}
              className={`subtab ${isActive ? 'active' : ''}`.trim()}
              onClick={() => onChange(tab.key)}
              onKeyDown={(e) => onKeyDown(e, tab)}
            >
              <span className="subtab-label">{tab.label}</span>
              {typeof tab.count === 'number' && (
                <span className="subtab-count">{tab.count}</span>
              )}
              {tab.badge && <span className="subtab-badge" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default SubTabs;
